import { Middleware, UnknownAction } from "@reduxjs/toolkit";
import { refreshNavPoint } from "@actions/useRefreshNavPoint";
import { selectPopulatedNavPoint } from "@selectors/getters";
import { AppStore } from "@redux/store/reduxStore";
import { NavPoint } from "@redux/navPoint/NavPoint";
import isEqual from "lodash/isEqual.js";

/**
 * Middleware to keep navPoint in sync with updates.
 *
 * It listens for any actions that might affect navPoint data and
 * automatically refreshes the navPoint with the latest data from the store.
 */
const navPointSyncMiddleware: Middleware = (store: AppStore) => next => (action: UnknownAction) => {
  const result = next(action);

  // Only run on the client side (not during SSR)
  if (typeof window === "undefined") {
    return result;
  }

  const state = store.getState();
  const currentNavPoint = state.navPoint;

  // Only process if we have a navPoint to sync
  if (!currentNavPoint) {
    return result;
  }

  // Check if this action might have affected entities that are part of the current navPoint
  if (shouldRefreshNavPoint(action, currentNavPoint)) {
    const refreshedNavPoint = selectPopulatedNavPoint(state);

    // Only dispatch if the data actually changed to avoid unnecessary re-renders
    if (hasNavPointChanged(currentNavPoint, refreshedNavPoint)) {
      store.dispatch(refreshNavPoint(refreshedNavPoint));
    }
  }

  return result;
};

/**
 * Determines if the given action might have affected objects in the current navPoint
 */
function shouldRefreshNavPoint(action: UnknownAction, _navPoint: NavPoint): boolean {
  const actionType = action.type;
  if (!actionType.includes("/fulfilled")) return false;

  return ["spaces", "items", "kits", "sections", "portals", "assets"].some(source => {
    const actionPattern = new RegExp(`${source}/(update|delete|create|move|duplicate|batch)`);
    return actionType.match(actionPattern);
  });
}

/**
 * Deep comparison to check if navPoint data has actually changed
 */
function hasNavPointChanged(oldNavPoint: NavPoint, newNavPoint: NavPoint): boolean {
  return !isEqual(oldNavPoint, newNavPoint);
}

export default navPointSyncMiddleware;
